import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Download, Calendar, Target, Layers, MessageCircle, BookOpen, Zap, Image, Mic, Move, History, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, ResponsiveContainer, Tooltip } from "recharts";
import { api, storage } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { robotoRegularBase64 } from "@/lib/robotoFont";
import { toast } from "sonner";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

const AREA_ICONS = {
  1: Target,
  2: Layers,
  3: MessageCircle,
  4: BookOpen,
  5: Zap,
  6: Image,
  7: Mic,
  8: Move,
};

export default function Results() {
  const navigate = useNavigate();
  const { assessmentId } = useParams();
  const { lang, lp, t } = useLang();
  const chartRef = useRef(null);

  const [assessment, setAssessment] = useState(null);
  const [config, setConfig] = useState({});
  const [loading, setLoading] = useState(true);
  const [showCalendarModal, setShowCalendarModal] = useState(false);
  const [contactEmail, setContactEmail] = useState("");

  const user = storage.getUser();

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const configData = await api.getConfig(lang);
        setConfig(configData);
        const data = await api.getAssessment(assessmentId, lang);
        setAssessment(data);
      } catch (error) {
        console.error(error);
        toast.error(t.results.errLoad);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [assessmentId, lang]);

  const handleBookSession = () => {
    if (config.calendar_url) {
      window.open(config.calendar_url, "_blank");
    } else {
      setShowCalendarModal(true);
    }
  };

  const handleContactSubmit = () => {
    if (!contactEmail) {
      toast.error(t.results.errEnterEmail);
      return;
    }
    toast.success(t.results.contactThanks);
    setShowCalendarModal(false);
    setContactEmail("");
  };

  const handleDownloadPDF = async () => {
    toast.info(t.results.generatingPDF);

    try {
      const pdf = new jsPDF("p", "mm", "a4");
      const pageWidth = pdf.internal.pageSize.getWidth();
      const P = t.results.pdf;

      if (lang === "ru") {
        pdf.addFileToVFS("Roboto-Regular.ttf", robotoRegularBase64);
        pdf.addFont("Roboto-Regular.ttf", "Roboto", "normal");
        pdf.setFont("Roboto");
      }

      // Title
      pdf.setFontSize(24);
      pdf.setTextColor(34, 34, 34);
      pdf.text(P.title, pageWidth / 2, 20, { align: "center" });

      // Branding
      pdf.setFontSize(10);
      pdf.setTextColor(128, 128, 128);
      pdf.text(t.branding, pageWidth / 2, 28, { align: "center" });

      // User info
      if (user) {
        pdf.setFontSize(12);
        pdf.setTextColor(34, 34, 34);
        pdf.text(`${P.name}: ${user.name}`, 20, 45);
        pdf.text(`${P.date}: ${new Date().toLocaleDateString(lang === "ru" ? "ru-RU" : "en-US")}`, 20, 52);
      }

      // Overall score
      pdf.setFontSize(16);
      pdf.text(`${P.overall}: ${assessment.overall_score}/80 (${assessment.overall_percentage}%)`, 20, 65);

      // Capture chart
      if (chartRef.current) {
        const canvas = await html2canvas(chartRef.current, { backgroundColor: "#222222" });
        const chartImg = canvas.toDataURL("image/png");
        pdf.addImage(chartImg, "PNG", 30, 75, 150, 100);
      }

      // Area scores
      pdf.setFontSize(14);
      pdf.text(P.areaScores, 20, 185);
      pdf.setFontSize(10);

      assessment.area_details.forEach((area, idx) => {
        const y = 195 + (idx * 7);
        pdf.text(`${area.area_name}: ${area.average_score}/10`, 25, y);
      });

      // Priority areas
      pdf.addPage();
      if (lang === "ru") pdf.setFont("Roboto");
      pdf.setFontSize(16);
      pdf.text(P.focusAreas, 20, 20);
      pdf.setFontSize(10);

      assessment.priorities.forEach((areaId, idx) => {
        const area = assessment.area_details.find(a => a.area_id === areaId);
        if (area) {
          const y = 35 + (idx * 12);
          pdf.setFontSize(12);
          pdf.text(`${idx + 1}. ${area.area_name} (${P.score}: ${area.average_score})`, 25, y);
        }
      });

      pdf.save(P.fileName);
      toast.success(t.results.pdfDone);
    } catch (error) {
      console.error(error);
      toast.error(t.results.pdfFail);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#222222] text-white flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-[#FFEA00] border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!assessment) {
    return (
      <div className="min-h-screen bg-[#222222] text-white flex items-center justify-center">
        <p>{t.results.notFound}</p>
      </div>
    );
  }

  const chartData = assessment.area_details.map(area => ({
    subject: area.area_name,
    score: area.average_score,
    fullMark: 10,
  }));

  const priorityAreas = assessment.priorities.map(areaId =>
    assessment.area_details.find(a => a.area_id === areaId)
  ).filter(Boolean);

  return (
    <div className="min-h-screen bg-[#222222] text-white">
      {/* Header */}
      <header className="px-6 py-4 flex justify-between items-center max-w-6xl mx-auto">
        <Button
          variant="ghost"
          onClick={() => navigate(lp("/"))}
          className="text-white/60 hover:text-white"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          {t.common.home}
        </Button>
        <div className="flex items-center gap-2">
          <LanguageSwitcher />
          {user && (
            <Button
              variant="ghost"
              onClick={() => navigate(lp("/history"))}
              className="text-white/60 hover:text-white"
              data-testid="history-btn"
            >
              <History className="w-4 h-4 mr-2" />
              {t.common.history}
            </Button>
          )}
          <Button
            variant="outline"
            onClick={handleDownloadPDF}
            className="border-white/30 text-white hover:bg-white/10"
            data-testid="download-pdf-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            {t.common.downloadPDF}
          </Button>
        </div>
      </header>

      <main className="px-6 py-8 max-w-6xl mx-auto">
        {/* Results Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl font-bold mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {t.results.title}
          </h1>
          {user && assessmentId !== "sample" && <p className="text-white/60">{t.results.resultsFor(user.name)}</p>}
          {assessmentId === "sample" && (
            <p className="text-[#FFEA00] mt-2">{t.results.sampleReport}</p>
          )}
        </motion.div>

        {/* Overall Score */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid md:grid-cols-2 gap-8 mb-12"
        >
          {/* Radar Chart */}
          <Card className="bg-[#2a2a2a] border-white/5" ref={chartRef}>
            <CardContent className="p-6">
              <ResponsiveContainer width="100%" height={350}>
                <RadarChart data={chartData} margin={{ top: 20, right: 30, bottom: 20, left: 30 }}>
                  <PolarGrid stroke="rgba(255,255,255,0.1)" />
                  <PolarAngleAxis
                    dataKey="subject"
                    tick={{ fill: '#fff', fontSize: 11 }}
                    tickLine={false}
                  />
                  <PolarRadiusAxis
                    angle={90}
                    domain={[0, 10]}
                    tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 10 }}
                    axisLine={false}
                  />
                  <Radar
                    name="Score"
                    dataKey="score"
                    stroke="#FFEA00"
                    fill="#FFEA00"
                    fillOpacity={0.3}
                    strokeWidth={2}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#333',
                      border: 'none',
                      borderRadius: '8px',
                      color: '#fff'
                    }}
                  />
                </RadarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Score Summary */}
          <div className="space-y-6">
            <Card className="bg-[#2a2a2a] border-white/5">
              <CardContent className="p-6 text-center">
                <p className="text-white/60 mb-2">{t.results.overallScore}</p>
                <div className="text-6xl font-bold text-[#FFEA00] mb-2 score-display">
                  {assessment.overall_score}
                  <span className="text-2xl text-white/40">/80</span>
                </div>
                <div className="text-3xl font-semibold">
                  {assessment.overall_percentage}%
                </div>
              </CardContent>
            </Card>

            <Card className="bg-[#2a2a2a] border-white/5">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">{t.results.focusAreas}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {priorityAreas.map((area, idx) => {
                  const Icon = AREA_ICONS[area.area_id] || Target;
                  return (
                    <div key={area.area_id} className="flex items-center gap-3">
                      <span className="w-6 h-6 rounded-full bg-[#FFEA00] text-black text-sm font-bold flex items-center justify-center">
                        {idx + 1}
                      </span>
                      <div
                        className="w-8 h-8 rounded-lg flex items-center justify-center"
                        style={{ backgroundColor: `${area.color}20` }}
                      >
                        <Icon className="w-4 h-4" style={{ color: area.color }} />
                      </div>
                      <span className="flex-1">{area.area_name}</span>
                      <span className="font-semibold" style={{ color: area.color }}>
                        {area.average_score}
                      </span>
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          </div>
        </motion.div>

        {/* All Area Cards */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mb-12"
        >
          <h2 className="text-2xl font-bold mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {t.results.allAreas}
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {assessment.area_details.map(area => {
              const Icon = AREA_ICONS[area.area_id] || Target;
              const isPriority = assessment.priorities.includes(area.area_id);
              return (
                <Card
                  key={area.area_id}
                  className={`bg-[#2a2a2a] border-white/5 ${isPriority ? 'ring-1 ring-[#FFEA00]/30' : ''}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3 mb-3">
                      <div
                        className="w-10 h-10 rounded-lg flex items-center justify-center"
                        style={{ backgroundColor: `${area.color}20` }}
                      >
                        <Icon className="w-5 h-5" style={{ color: area.color }} />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold">{area.area_name}</h3>
                        {isPriority && (
                          <span className="text-xs text-[#FFEA00]">{t.results.focusAreaBadge}</span>
                        )}
                      </div>
                      <span
                        className="text-2xl font-bold score-display"
                        style={{ color: area.color }}
                      >
                        {area.average_score}
                      </span>
                    </div>
                    <p className="text-sm text-white/50 line-clamp-2">{area.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </motion.div>

        {/* CTA: View Program */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="text-center mb-12"
        >
          <Button
            onClick={() => navigate(lp(`/program/${assessmentId}`))}
            className="bg-[#FFEA00] hover:bg-[#FFD600] text-black font-semibold px-8 py-6 text-lg rounded-lg transition-all hover:scale-105"
            data-testid="view-program-btn"
          >
            {t.results.viewProgram}
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </motion.div>

        {/* Booking CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-gradient-to-r from-[#2a2a2a] to-[#333] rounded-2xl p-8 text-center"
        >
          <h2 className="text-2xl font-bold mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {t.results.discussTitle}
          </h2>
          <p className="text-white/60 mb-6 max-w-lg mx-auto">
            {t.results.discussSubtitle}
          </p>
          <Button
            onClick={handleBookSession}
            className="bg-[#FFEA00] hover:bg-[#FFD600] text-black font-semibold px-8 py-4 rounded-lg transition-all hover:scale-105"
            data-testid="book-session-btn"
          >
            <Calendar className="mr-2 w-5 h-5" />
            {t.results.bookSession}
            <ExternalLink className="ml-2 w-4 h-4" />
          </Button>
          <p className="text-sm text-white/40 mt-4">{t.branding}</p>
        </motion.div>
      </main>

      {/* Calendar Modal */}
      <Dialog open={showCalendarModal} onOpenChange={setShowCalendarModal}>
        <DialogContent className="bg-[#2a2a2a] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle>{t.results.calModalTitle}</DialogTitle>
            <DialogDescription className="text-white/60">
              {t.results.calModalDesc}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <Input
              type="email"
              placeholder="your@email.com"
              value={contactEmail}
              onChange={(e) => setContactEmail(e.target.value)}
              className="bg-[#333] border-white/10 text-white"
            />
            <Button
              onClick={handleContactSubmit}
              className="w-full bg-[#FFEA00] hover:bg-[#FFD600] text-black font-semibold"
            >
              {t.common.submit}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
